import React, { useState } from "react";
import { StatCard, MetaCell, Expandable, DataTable, SectionTitle, LastSynced, EmptyState } from "./DashboardPrimitives";

const mapAccess = (lvl) =>
  lvl === 50 ? "Owner" : lvl === 40 ? "Maintainer" : lvl === 30 ? "Developer" : lvl === 20 ? "Reporter" : "Guest";

const GitLabDashboard = ({ data, formatLastConnected }) => {
  const [openProject, setOpenProject] = useState(null);
  const toggleProject = (id) => setOpenProject(openProject === id ? null : id);

  if (!data?.connector_data) return <EmptyState message="No GitLab data available" />;

  const { identity, repositories } = data.connector_data;

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
        {identity.avatar_url && (
          <img src={identity.avatar_url} alt="GitLab" className="w-10 h-10 rounded-full border border-gray-200 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">{identity.name} <span className="text-gray-400 font-normal">@{identity.username}</span></p>
          <LastSynced value={data?.last_synced_at} formatFn={formatLastConnected} />
        </div>
      </div>

      {/* Security snapshot */}
      <div>
        <SectionTitle title="Security Snapshot" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="2FA Enabled" value={identity.two_factor_enabled ? "Yes" : "No"} danger={!identity.two_factor_enabled} />
          <StatCard label="Account Locked" value={identity.locked ? "Yes" : "No"} danger={identity.locked} />
          <StatCard label="Can Create Projects" value={identity.can_create_project ? "Yes" : "No"} />
          <StatCard label="Can Create Groups" value={identity.can_create_group ? "Yes" : "No"} />
        </div>
      </div>

      {/* Projects */}
      <div className="space-y-3">
        <SectionTitle title="Projects" count={repositories.length} />
        {repositories.map((p) => {
          const repo = p.repository;
          return (
            <Expandable
              key={repo.id}
              title={repo.name}
              isOpen={openProject === repo.id}
              onToggle={() => toggleProject(repo.id)}
              badge={
                <div className="flex gap-1.5">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium text-white capitalize"
                    style={{ backgroundColor: repo.visibility === "private" ? "#6B7280" : "var(--app-primary-color, #26256b)" }}
                  >
                    {repo.visibility}
                  </span>
                  {repo.marked_for_deletion_at && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">Deletion Scheduled</span>
                  )}
                </div>
              }
            >
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <MetaCell label="Default Branch" value={repo.default_branch} />
                  <MetaCell label="Last Activity" value={repo.last_activity_at} />
                  <MetaCell label="CI Enabled" value={repo.jobs_enabled ? "Yes" : "No"} />
                </div>
                <DataTable
                  title="Branches"
                  headers={["Name", "Protected", "Default"]}
                  rows={p.branches.map((b) => [b.name, b.protected ? "Yes" : "No", b.default ? "Yes" : "No"])}
                />
                <DataTable
                  title="Members"
                  headers={["User", "Access Level", "State"]}
                  rows={p.members.map((m) => [m.username, mapAccess(m.access_level), m.state])}
                />
              </div>
            </Expandable>
          );
        })}
      </div>
    </div>
  );
};

export default GitLabDashboard;
